import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError, api } from '../api';
import { cents, money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert } from '../layout';

/** Uploads the signed cash order (PDF or a photo) to a KPO/KIO. */
export async function uploadCashFile(entryId: number, file: File): Promise<void> {
  const body = new FormData();
  body.append('file', file, file.name);
  const response = await fetch(`api/v1/cash/entries/${entryId}/file`, { method: 'POST', body, credentials: 'same-origin', headers: { 'x-fsp-request': '1' } });
  if (!response.ok) {
    const error = ((await response.json().catch(() => null)) as { error?: { code?: string } } | null)?.error;
    throw new ApiError(response.status, error?.code ?? 'upload_failed');
  }
}

export interface CashTarget { id: number; label: string; buyer: string; left: number }

/**
 * Paid in cash: a cash receipt order (KPO) for the whole amount still owed, with the signed order uploaded if
 * there is one. This is, with a bank payment, the only way an issued invoice becomes paid.
 */
export function CashReceiptDialog({ invoice, onClose }: { invoice: CashTarget | null; onClose: () => void }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const dialog = useRef<HTMLDialogElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const save = useMutation({
    mutationFn: async (body: { day: string; amount: number; party: string; purpose: string }) => {
      const { entry } = await api.post<{ entry: { id: number } }>('api/v1/cash/entries', { ...body, direction: 'in', invoiceId: invoice!.id });
      if (file) await uploadCashFile(entry.id, file);
    },
    onSuccess: async () => {
      await Promise.all(['invoices', 'invoice-summary', 'dashboard', 'cash', 'partners'].map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
      onClose();
    },
  });
  useEffect(() => {
    if (invoice) { setFile(null); save.reset(); dialog.current?.showModal(); } else dialog.current?.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const d = new FormData(event.currentTarget);
    let amount = 0;
    try { amount = cents(String(d.get('amount'))); } catch { /* checked below */ }
    if (!Number.isFinite(amount) || amount <= 0) return;
    save.mutate({ day: String(d.get('day')), amount, party: String(d.get('party') ?? '').trim(), purpose: String(d.get('purpose') ?? '').trim() });
  };
  return (
    <dialog ref={dialog} className="confirm-dialog send-dialog" onClose={onClose}>
      {invoice ? (
        <form className="dialog-card" onSubmit={submit}>
          <p className="eyebrow">{t('Kasos pajamų orderis (KPO)')}</p>
          <h2>{t('Apmokėta grynaisiais')} · {invoice.label}</h2>
          {save.isError ? <Alert kind="error">{errorText(t, save.error)}</Alert> : null}
          <label>{t('Data')}<input type="date" name="day" defaultValue={new Date().toLocaleDateString('sv-SE')} required /></label>
          <label>{t('Suma')}<input name="amount" inputMode="decimal" defaultValue={(invoice.left / 100).toFixed(2).replace('.', ',')} required /></label>
          <label>{t('Iš ko')}<input name="party" defaultValue={invoice.buyer} maxLength={200} /></label>
          <label>{t('Turinys')}<input name="purpose" defaultValue={`Apmokėta ${invoice.label}`} required maxLength={500} /></label>
          <label>{t('Pasirašytas KPO (PDF ar nuotrauka, nebūtina)')}<input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></label>
          <p className="muted">{t('Orderis įrašomas į kasos knygą ir sąskaita tampa apmokėta. Suma turi padengti visą likutį ({amount}).', { amount: money(invoice.left) })}</p>
          <div className="dialog-actions">
            <button type="button" className="button button--ghost" onClick={onClose}>{t('Atšaukti')}</button>
            <button type="submit" className="button button--primary" disabled={save.isPending}>{t('Įrašyti KPO')}</button>
          </div>
        </form>
      ) : null}
    </dialog>
  );
}
